/**
 * A document's plays: writing one, and reading them back as their author.
 *
 * The two ends of one wire, in one file, because they are one idea — a learner's
 * answers to a document's pages go up, and the author's summary of them comes
 * down. The payload is the *same* shape a FlashBook play uses
 * (`FlashBookPageResult`), so scoring, reports and any future class report read a
 * document's results with the code they already have rather than a second dialect.
 * One field is a document's own: beside the per-page results a play carries how many
 * of the document's *pages* the read has been through, because a document is mostly
 * pages that ask nothing and a report that could only count answers could not tell
 * page 4 from page 24 (`pagesReadForSubmission`).
 *
 * Three refusals worth naming, all inherited from the FlashBook play route:
 *
 *  - **A server that has not been taught the method is not an error.** It answers
 *    501 or 404; both are reported as *no plays*, so the author's summary says
 *    nobody has played yet rather than throwing at them. The feature switches
 *    itself on when the backend that implements it is deployed.
 *  - **A play is only written when there is a session.** A learner reading
 *    signed out has nowhere to attribute a result to, and the reader must keep
 *    working for them regardless.
 *  - **A failed write is not retried by the reader.** The read is over; a failed
 *    upload of a score is worth a log, not a modal. The results are kept in the
 *    device store by the caller if they need to be kept at all.
 */
import {useMutation, useQuery} from '@tanstack/react-query'

import {BSKY_SERVICE} from '#/lib/constants'
import {type DocumentPlay} from '#/lib/document/report'
import {createQueryKey} from '#/state/queries/util'
import {useAgent} from '#/state/session'
import {type FlashBookPageResult} from '#/types/flashbook'

export function useCreateDocumentPlayMutation() {
  const agent = useAgent()
  return useMutation({
    mutationFn: async ({
      documentUri,
      title,
      score,
      maxPoints,
      timeMs,
      pageResults,
      pagesSeen,
    }: {
      documentUri: string
      /** The document's title, so a report can name what was played. */
      title?: string
      score: number
      maxPoints: number
      timeMs: number
      pageResults: FlashBookPageResult[]
      /**
       * How many pages of the document this read has been through.
       *
       * Sent beside the answers because the two say different things: the results are
       * the pages that asked something, and a reader who got to page 24 of a deck whose
       * last question is on page 9 has answers on pages 1–9 and has read twenty-four.
       * It is the author's half of `#/lib/document/progress`, which no answer can carry
       * — see `pagesReadForSubmission`.
       */
      pagesSeen?: number
    }) => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')

      const res = await fetch(
        `${BSKY_SERVICE}/xrpc/app.bsky.feed.createDocumentPlay`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessJwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            documentUri,
            title,
            score,
            maxPoints,
            timeMs,
            pageResults,
            pagesSeen,
          }),
        },
      )

      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as {message?: string}
        throw new Error(err.message ?? `HTTP ${res.status}`)
      }

      return (await res.json()) as {playId: string}
    },
  })
}

const documentPlaysQueryKeyRoot = 'documentPlays'

export const createDocumentPlaysQueryKey = (documentUri: string) =>
  createQueryKey(documentPlaysQueryKeyRoot, {documentUri})

/** One play as the server lists it, before it has been cleaned up. */
type PlayView = {
  player?: {
    did?: unknown
    handle?: unknown
    displayName?: unknown
    avatar?: unknown
    viewer?: unknown
  }
  score?: unknown
  maxPoints?: unknown
  timeMs?: unknown
  pageResults?: unknown
  /** Pages the read has been through, when the row says so. */
  pagesSeen?: unknown
  createdAt?: unknown
}

/**
 * The viewer's relationship to one player, as far as the row states it.
 *
 * The plays route hydrates each player *as the viewer sees them*, which is what
 * makes the card able to say "someone you follow has played this" without asking
 * about anybody a second time. Only the two follow edges are read; the rest of the
 * viewer state belongs to the profile views that already carry it.
 */
function relationOf(raw: unknown): {following?: string; followedBy?: string} {
  if (!raw || typeof raw !== 'object') return {}
  const viewer = raw as {following?: unknown; followedBy?: unknown}
  return {
    following:
      typeof viewer.following === 'string' ? viewer.following : undefined,
    followedBy:
      typeof viewer.followedBy === 'string' ? viewer.followedBy : undefined,
  }
}

/**
 * A play the report can read, or `null` for one it cannot.
 *
 * A row with no player cannot be attributed and is dropped rather than printed as
 * "someone" — the same rule the FlashBook class report applies. Everything else
 * is read leniently: a missing score is a play that happened and cannot be
 * compared, which is a real thing a report has to be able to say.
 */
function documentPlayOf(view: PlayView): DocumentPlay | null {
  const did = typeof view.player?.did === 'string' ? view.player.did.trim() : ''
  if (!did) return null
  const handle =
    typeof view.player?.handle === 'string' ? view.player.handle : undefined
  return {
    playerDid: did,
    playerHandle: handle,
    /*
     * The whole player, rebuilt from what the row actually said rather than cast:
     * a deployment that hydrates less than we expect still yields a usable profile
     * (the DID, and `handle.invalid` for a name nobody could resolve), so a card
     * showing faces never has to defend itself against a half-answered request.
     */
    player: {
      did,
      handle: handle ?? 'handle.invalid',
      displayName:
        typeof view.player?.displayName === 'string'
          ? view.player.displayName
          : undefined,
      avatar:
        typeof view.player?.avatar === 'string'
          ? view.player.avatar
          : undefined,
      viewer: relationOf(view.player?.viewer),
    },
    score: typeof view.score === 'number' ? view.score : undefined,
    maxPoints: typeof view.maxPoints === 'number' ? view.maxPoints : undefined,
    /*
     * A row that does not say how far the read got is a row from a server (or a
     * client) that predates the count, and it is left *absent* rather than set to
     * nought, so the board can floor it by the pages the read answered rather than
     * reading "no pages" into a read that was plainly through several (`pagesRead`).
     */
    pagesSeen:
      typeof view.pagesSeen === 'number' && view.pagesSeen >= 0
        ? Math.floor(view.pagesSeen)
        : undefined,
    timeMs: typeof view.timeMs === 'number' ? view.timeMs : undefined,
    pageResults: Array.isArray(view.pageResults)
      ? (view.pageResults as DocumentPlay['pageResults'])
      : [],
    createdAt: typeof view.createdAt === 'string' ? view.createdAt : undefined,
  }
}

/**
 * The plays of a document, newest first.
 *
 * Two callers, and they ask for different reasons: the author opens the summary to
 * see how their document is doing, and the card itself asks so it can say who has
 * played — a document with questions is a thing other people are working through,
 * and knowing that is what makes a reader start. Both are the same request, so both
 * share one cache entry.
 *
 * `enabled` is always the caller's own question. The author's dialog drives it from
 * whether they actually opened the summary — it is mounted with the card, on native
 * before it is ever presented — so a feed of a teacher's own documents makes no
 * requests until one is asked about; the card drives it from whether it is on screen
 * *and* signed in, so a signed-out reader asks nothing and a column of cards asks
 * only for the few a reader has actually scrolled to.
 *
 * Both ends go to `BSKY_SERVICE`, the same door the feed, the notifications and
 * every other appview read in this app go through. It is the door that already
 * carries the reader's session — this route is asked who is asking — and it is the
 * one that answers wherever the app runs, including a deployment whose appview is
 * not directly addressable.
 */
export function useDocumentPlaysQuery(
  documentUri: string | undefined,
  {enabled = true}: {enabled?: boolean} = {},
) {
  const agent = useAgent()
  return useQuery({
    queryKey: createDocumentPlaysQueryKey(documentUri ?? ''),
    enabled: !!documentUri && enabled,
    queryFn: async (): Promise<DocumentPlay[]> => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')

      const params = new URLSearchParams({
        documentUri: documentUri!,
        limit: '100',
      })
      const res = await fetch(
        `${BSKY_SERVICE}/xrpc/app.bsky.feed.getDocumentPlays?${params}`,
        {headers: {Authorization: `Bearer ${accessJwt}`}},
      )
      if (!res.ok) {
        if (res.status === 501 || res.status === 404) return []
        const err = (await res.json().catch(() => ({}))) as {message?: string}
        throw new Error(err.message ?? `HTTP ${res.status}`)
      }
      const data = (await res.json()) as {plays?: PlayView[]}
      const plays: DocumentPlay[] = []
      for (const view of data.plays ?? []) {
        const play = documentPlayOf(view)
        if (play) plays.push(play)
      }
      return plays
    },
    staleTime: 1000 * 60,
  })
}
